import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from '../../../genericDashboard/gzGenericWidget/types';
import { WidgetHeaderItemSize } from '../types';
import { classMap } from 'lit/directives/class-map.js';
import { formatLargeNumber } from '../../../utils/formatter.utils';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ButtonColor } from '../../gzIconButton/types';

@customElement('gz-widget-header-item')
export class GzWidgetHeaderItemWebComponent extends LitElement {
  @property({ type: String, attribute: true, reflect: true })
  name: string;

  @property({ type: Number, attribute: true, reflect: true })
  value: number;

  @property({ type: String, attribute: true, reflect: true })
  iconClass: string;

  @property({ type: WidgetHeaderItemSize, attribute: true, reflect: true })
  size: WidgetHeaderItemSize;

  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Boolean, attribute: true, reflect: true })
  decreaseNameFont = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  clickable = false;

  static get styles() {
    return styles;
  }

  render(): TemplateResult {
    const itemClasses = {
      'header-item': true,
      center: this.size === WidgetHeaderItemSize.SMALL,
      'fs-18': this.size === WidgetHeaderItemSize.SMALL,
      'line-height-32': this.size === WidgetHeaderItemSize.SMALL && !this.smallSizeWithNoIcon(),
      'fs-12': this.size !== WidgetHeaderItemSize.SMALL,
      'fw-500': this.size !== WidgetHeaderItemSize.SMALL || this.smallSizeWithNoIcon(),
    };
    const topRowClasses = {
      'header-top-row': true,
      center: this.size === WidgetHeaderItemSize.SMALL,
      'fs-24': this.size === WidgetHeaderItemSize.MEDIUM,
      'fs-32': this.size === WidgetHeaderItemSize.BIG,
    };
    return html`
      <div class="${classMap(itemClasses)}">
        <div class="${classMap(topRowClasses)}">${this.renderIcon()} ${this.renderTopRowValue()}</div>
        ${this.renderBottomRowValue()}
      </div>
    `;
  }

  private renderTopRowValue(): TemplateResult | undefined {
    if (this.size === WidgetHeaderItemSize.SMALL && !this.smallSizeWithNoIcon()) {
      return;
    }
    return this.isActiveState() ? html`${formatLargeNumber(this.value)}` : html`-`;
  }

  private renderBottomRowValue(): TemplateResult {
    if (this.size === WidgetHeaderItemSize.SMALL && !this.smallSizeWithNoIcon()) {
      return html`${this.isActiveState() ? formatLargeNumber(this.value) : '-'}`;
    }
    const classes = {
      'fs-9': this.decreaseNameFont && this.size !== WidgetHeaderItemSize.BIG,
      'fs-10': this.decreaseNameFont && this.size === WidgetHeaderItemSize.BIG,
      'fs-12': !this.decreaseNameFont,
    };
    return html`<gz-tooltip class="${classMap(classes)}" isEllipsis tooltipText="${this.name}"
      >${this.name}</gz-tooltip
    >`;
  }

  private smallSizeWithNoIcon(): boolean {
    return this.size === WidgetHeaderItemSize.SMALL && !this.iconClass;
  }

  private getSeverityIconColor(iconClass: string): string | null {
    const matches = iconClass.match(/[^_]*$/);
    return matches ? (matches[0] === ButtonColor.NEGATIVE ? 'var(--red-layout-300-primary)' : matches[0]) : null;
  }

  private renderIcon(): TemplateResult | undefined {
    if (!this.iconClass) {
      return;
    }

    const iconColor: string | null = this.getSeverityIconColor(this.iconClass);
    const classes = {
      'gray-color': !this.isActiveState(),
    };
    return this.size === WidgetHeaderItemSize.SMALL
      ? html`<gz-tooltip class="d-flex" tooltipText="${this.name}"
          ><gz-icon
            class="${classMap(classes)}"
            size="24"
            name="${this.iconClass}"
            color="${ifDefined(iconColor)}"
          ></gz-icon
        ></gz-tooltip>`
      : html`<gz-icon
          class="${classMap(classes)}"
          size="24"
          name="${this.iconClass}"
          color="${ifDefined(iconColor)}"
        ></gz-icon>`;
  }

  private isActiveState(): boolean {
    return this.state === WidgetStateFrontEnd.IS_ACTIVE;
  }
}
